// NextButton — platform-adapted save/advance control.
//
// Save Current Frame marks the current frame saved, writes the local draft,
// and advances to the next unsaved frame. When every auditable frame is saved,
// the shared completion dialog can submit through the SecPose platform commit
// handler instead of the upstream file-save path.

import * as React from 'react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { t } from '../../config/strings';
import { buildAdjustedToon } from '../../io/serialize';
import { advanceFrame } from '../../lib/advance';
import { useStore } from '../../store';
import { episodeElapsedMs } from '../../store/video';

export interface NextButtonProps {
  onSubmit: (toonText: string, frameCount: number, elapsedMs: number) => Promise<void>;
  submitting?: boolean;
}

export function NextButton({ onSubmit, submitting = false }: NextButtonProps): React.ReactElement {
  const hasFolder = useStore((s) => s.folder !== null);
  const totalFrames = useStore((s) => s.totalFrames);
  const timerBaseMs = useStore((s) => s.timerBaseMs);
  const timerStartedAt = useStore((s) => s.timerStartedAt);
  const completionOpen = useStore((s) => s.completionOpen);
  const setCompletionOpen = useStore((s) => s.setCompletionOpen);

  const [busy, setBusy] = React.useState(false);
  const [dialogBusy, setDialogBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const submitBusy = submitting || dialogBusy;

  const handleNext = async (): Promise<void> => {
    setBusy(true);
    try {
      await advanceFrame();
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitFromDialog = async (): Promise<void> => {
    if (!useStore.getState().folder) return;
    setDialogBusy(true);
    setError(null);
    try {
      const store = useStore.getState();
      store.commitFrameSeeds(store.currentFrame);
      const { toonText, frameCount } = buildAdjustedToon();
      const elapsedMs = episodeElapsedMs({ timerBaseMs, timerStartedAt });
      await onSubmit(toonText, frameCount, elapsedMs);
      setCompletionOpen(false);
    } catch (err) {
      console.error('pose submit failed:', err);
      setError(err instanceof Error ? err.message : 'Submit failed');
    } finally {
      setDialogBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="default"
        disabled={!hasFolder || totalFrames === 0 || busy || submitting}
        onClick={handleNext}
        data-testid="next-button"
      >
        {t.nextBtn.next}
      </Button>

      <Dialog open={completionOpen} onOpenChange={setCompletionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.nextBtn.completionTitle}</DialogTitle>
            <DialogDescription>{t.nextBtn.completionDesc}</DialogDescription>
          </DialogHeader>
          {error !== null && (
            <p className="text-xs text-red-500" data-testid="completion-error">
              {t.nextBtn.errorPrefix}
              {error}
            </p>
          )}
          <DialogFooter>
            <Button variant="ghost" disabled={submitBusy} onClick={() => setCompletionOpen(false)}>
              {t.nextBtn.back}
            </Button>
            <Button
              variant="default"
              disabled={submitBusy}
              onClick={handleSubmitFromDialog}
              data-testid="completion-submit"
            >
              {submitBusy ? t.nextBtn.saving : t.nextBtn.submit}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
